import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, LogOut } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser, loginWithGoogle } from "../../app/features/auth";
import { resetLocation } from "../../app/features/location";
import Button from "../ui/Button";
// import gogole icon form material ui
import { Google } from "@mui/icons-material";

const Navigation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session, user } = useSelector((state) => state.auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      dispatch(resetLocation());
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await dispatch(loginWithGoogle()).unwrap();
      // OAuth will redirect, so we don't need to navigate
    } catch (error) {
      console.error("Error logging in with Google:", error);
    }
  };

  const navLinks = [
    { label: "Generator", href: "/personality-analysis" },
    { label: "Features", href: "/#features" },
    { label: "Pricing", href: "/pricing" },
    { label: "FAQs", href: "/#faq" },
  ];

  const handleLinkClick = (e, href) => {
    e.preventDefault();

    // Check if href contains a hash
    if (href.includes("#")) {
      const [path, hash] = href.split("#");
      const targetPath = path || "/";
      const hashId = hash;

      // If we're already on the target path, just scroll
      if (window.location.pathname === targetPath) {
        setTimeout(() => {
          const element = document.getElementById(hashId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        // Navigate to the path first, then scroll to hash
        navigate(targetPath);
        setTimeout(() => {
          const element = document.getElementById(hashId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 500);
      }
    } else if (href.startsWith("/")) {
      // Regular route navigation
      navigate(href);
    } else {
      // External link or other
      window.location.href = href;
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-stone-800 bg-stone-950/60 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate("/")}
        >
          <div className="w-8 h-8 bg-gradient-to-br from-rose-600 to-orange-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold tracking-tight text-white">
            FaceTraits
          </span>
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleLinkClick(e, link.href)}
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          {session ? (
            <>
              {user && (
                <button
                  onClick={() => navigate("/subscription")}
                  className="text-sm text-zinc-400 hover:text-white transition-colors cursor-pointer"
                >
                  {user.email || user.user_metadata?.email || "User"}
                </button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                onClick={handleGoogleLogin}
                className="flex items-center gap-2 bg-white text-black hover:bg-gray-100"
              >
                <Google className="w-4 h-4 text-black mr-2" /> Login with Google
              </Button>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-zinc-300"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-stone-800 bg-stone-950 p-4 space-y-4">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleLinkClick(e, link.href)}
              className="block text-sm text-zinc-300 py-2 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <div className="pt-4 border-t border-zinc-800 flex flex-col gap-2">
            {session ? (
              <>
                {user && (
                  <button
                    onClick={() => navigate("/subscription")}
                    className="text-sm text-zinc-400 hover:text-white transition-colors cursor-pointer text-left px-2 py-2"
                  >
                    {user.email || user.user_metadata?.email || "User"}
                  </button>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start flex items-center gap-2"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4" />
                  Log out
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="w-full justify-start"
                  onClick={handleGoogleLogin}
                >
                  Login with Google
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate("/personality-analysis")}
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
